import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAnnualProjectPrograms } from '../services/annualProjectProgramsService';
import { getCalendarDays } from '../services/calendarService';
import { getGradeTrackSections, loadGradeTrackSectionsCache } from '../services/sectionLayoutService';
import type { AcademicTerm, AnnualWeekdayKey, DayKind, Grade } from '../types/domain';
import {
  loadAnnualProgressDataCache,
  saveAnnualProgressDataCache,
  type OffDayByDate,
} from '../utils/annualProgressDataCache';
import { hasAnnualProjectProgramsCacheEntry, loadAnnualProjectPrograms } from '../utils/annualProjectProgramsStorage';
import {
  buildDashboardAnnualDaySlotsFromLoadedData,
  loadDashboardLessonTermDataFromCaches,
  mergeDashboardLessonTermData,
  type DashboardAnnualDaySlot,
  type DashboardLessonSectionBundle,
  type DashboardLessonSectionTermData,
} from '../utils/dashboardAnnualDaySlots';
import { resolveTermNumberForViewDate } from '../utils/dashboardLessonSectionResolve';
import { consumePostLoginSchoolDataNetworkRefresh } from '../utils/postLoginSchoolDataRefresh';
import { useAnnualProgressMetaQuery } from '../query/hooks/useAnnualProgressMetaQuery';
import { useAnnualTimetableCellsQuery } from '../query/hooks/useAnnualTimetableCellsQuery';
import { useCalendarDaysQuery } from '../query/hooks/useCalendarDaysQuery';
import { useProgramFixedRowsQuery } from '../query/hooks/useProgramFixedRowsQuery';
import { useProgramProgressCellsQuery } from '../query/hooks/useProgramProgressCellsQuery';
import { useProgramScopedRowsQuery } from '../query/hooks/useProgramScopedRowsQuery';
import { schoolKeys } from '../query/keys';
import { lsInitialDataOptions } from '../query/hooks/lsInitialData';
import { saveProgramProgressCellsCache } from '../utils/programProgressCellsCache';
import { saveAnnualProgressScopedTermCache } from '../utils/annualProgressScopedTermCache';
import { saveAnnualTimetableGradeCellsLocalCache } from '../services/annualTimetableService';
import { saveDashboardLessonDaySlotsSnapshot } from '../utils/dashboardLessonDaySlotsSnapshot';
import { useCalendarLinkedAcademicYear } from './useCalendarLinkedAcademicYear';
import { useCalendarLinkedContext } from './useCalendarLinkedContext';

function calendarDaysToOffDayState(days: Awaited<ReturnType<typeof getCalendarDays>>): {
  holidayDays: string[];
  offDayByDate: OffDayByDate;
  dayKindByDate: Record<string, DayKind>;
} {
  const offDayByDate: OffDayByDate = {};
  const holidayDays: string[] = [];
  const dayKindByDate: Record<string, DayKind> = {};
  for (const d of days) {
    const day = (d.day ?? '').trim();
    if (!day) continue;
    dayKindByDate[day] = d.day_kind;
    if (d.day_kind === 'holiday' || d.day_kind === 'closure' || d.day_kind === 'vacation') {
      holidayDays.push(day);
      const note = (d.note ?? '').trim();
      offDayByDate[day] = note ? { kind: d.day_kind, note } : { kind: d.day_kind };
    }
  }
  return { holidayDays, offDayByDate, dayKindByDate };
}

/** grades LS miss여도 schedule.section.gradeIds 매칭용 stub */
function gradesWithStub(grades: Grade[], workspaceId: string, gradeId: string): Grade[] {
  if (grades.some((g) => g.id === gradeId)) return grades;
  if (!gradeId.trim()) return grades;
  return [
    ...grades,
    {
      id: gradeId,
      workspace_id: workspaceId,
      name: '',
      sort_order: 0,
    },
  ];
}

/**
 * 대시보드 상단 '오늘 수업' 섹션 데이터. REST(RQ) + LS 캐시를 함께 사용한다.
 * 계산이 끝나면 메인 프레젠테이션 탭이 재사용할 수 있도록 daySlots 스냅샷을 LS+BroadcastChannel로 저장한다
 * (`src/hooks/usePresentationLessonDaySlots.ts` 참고).
 */
export function useDashboardLessonSectionData(params: {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey | null;
  dateIso: string;
  enabled: boolean;
  refreshNonce?: number;
  /** false: 로그인 직후 강제 네트워크 플래그를 소비하지 않음(대시보드 탭 몫) */
  consumePostLoginRefresh?: boolean;
  /** false: 메인 프레젠용 daySlots 스냅샷을 LS·BroadcastChannel로 내보내지 않음 */
  publishPresentationSnapshot?: boolean;
}): {
  daySlots: DashboardAnnualDaySlot[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  activeTermNumber: 1 | 2 | null;
  academicYear: number;
  terms: AcademicTerm[];
  lessonBundle: DashboardLessonSectionBundle | null;
} {
  const {
    workspaceId,
    gradeId,
    grades,
    mondayIso,
    weekdayKey,
    dateIso,
    enabled,
    refreshNonce = 0,
    consumePostLoginRefresh = true,
    publishPresentationSnapshot = true,
  } = params;

  const calendarLinkedAcademicYear = useCalendarLinkedAcademicYear(workspaceId, gradeId);
  const calendarLinkedCtx = useCalendarLinkedContext(workspaceId, gradeId);
  const queryClient = useQueryClient();
  const queryEnabled = enabled && !!workspaceId.trim() && !!gradeId.trim();
  const gradesForSlots = useMemo(
    () => gradesWithStub(grades, workspaceId, gradeId),
    [grades, workspaceId, gradeId]
  );
  const [forceNetwork, setForceNetwork] = useState(
    () => consumePostLoginRefresh && consumePostLoginSchoolDataNetworkRefresh(workspaceId)
  );

  useEffect(() => {
    if (consumePostLoginRefresh && consumePostLoginSchoolDataNetworkRefresh(workspaceId)) {
      setForceNetwork(true);
    }
  }, [consumePostLoginRefresh, workspaceId]);

  const metaQuery = useAnnualProgressMetaQuery({
    workspaceId,
    academicYear: calendarLinkedAcademicYear,
    termNumber: 1,
    gradeId,
    enabled: queryEnabled,
  });

  const terms: AcademicTerm[] = metaQuery.data?.terms ?? [];
  const activeTermNumber = useMemo(
    () => (terms.length > 0 ? resolveTermNumberForViewDate(terms, dateIso) : resolveTermNumberForViewDate([], dateIso)),
    [dateIso, terms]
  );

  const termDef = useMemo(
    () => terms.find((t) => t.term_number === activeTermNumber) ?? null,
    [activeTermNumber, terms]
  );

  const timetableQuery = useAnnualTimetableCellsQuery({
    workspaceId,
    academicYear: calendarLinkedAcademicYear,
    termNumber: activeTermNumber,
    gradeId,
    enabled: queryEnabled,
    forceNetwork,
  });

  const progressQuery = useProgramProgressCellsQuery({
    workspaceId,
    academicYear: calendarLinkedAcademicYear,
    termNumber: activeTermNumber,
    gradeId,
    enabled: queryEnabled,
    forceRefresh: forceNetwork,
  });

  const scopedRowsQuery = useProgramScopedRowsQuery({
    workspaceId,
    academicYear: calendarLinkedAcademicYear,
    termNumber: activeTermNumber,
    gradeId,
    enabled: queryEnabled,
    forceRefresh: forceNetwork,
  });

  const fixedRowsQuery = useProgramFixedRowsQuery({
    workspaceId,
    academicYear: calendarLinkedAcademicYear,
    termNumber: activeTermNumber,
    gradeId,
    enabled: queryEnabled,
    forceRefresh: forceNetwork,
  });

  const calendarQuery = useCalendarDaysQuery({
    workspaceId,
    from: termDef?.starts_on ?? '',
    to: termDef?.ends_on ?? '',
    enabled: queryEnabled && !!termDef?.starts_on && !!termDef?.ends_on,
  });

  const gradeTrackSectionsCache = loadGradeTrackSectionsCache(workspaceId);
  const gradeTrackSectionsQuery = useQuery({
    queryKey: schoolKeys.gradeTrackSections(workspaceId),
    enabled: queryEnabled,
    ...lsInitialDataOptions(gradeTrackSectionsCache != null, gradeTrackSectionsCache ?? []),
    queryFn: () => getGradeTrackSections(workspaceId),
  });

  const projectProgramsLs = loadAnnualProjectPrograms(workspaceId, gradeId, calendarLinkedAcademicYear);
  const annualProjectProgramsQuery = useQuery({
    queryKey: schoolKeys.annualProjectPrograms(workspaceId, calendarLinkedAcademicYear, gradeId),
    enabled: queryEnabled,
    ...lsInitialDataOptions(
      hasAnnualProjectProgramsCacheEntry(workspaceId, gradeId, calendarLinkedAcademicYear),
      projectProgramsLs
    ),
    queryFn: () => getAnnualProjectPrograms(workspaceId, gradeId, calendarLinkedAcademicYear),
  });

  const reload = useCallback(() => {
    if (!workspaceId.trim() || !gradeId.trim()) return;
    void queryClient.invalidateQueries({
      queryKey: schoolKeys.annualProgressMeta(workspaceId, calendarLinkedAcademicYear, 1, gradeId),
    });
    void queryClient.invalidateQueries({
      queryKey: schoolKeys.annualTimetableCells(
        workspaceId,
        calendarLinkedAcademicYear,
        activeTermNumber,
        gradeId
      ),
    });
    void queryClient.invalidateQueries({
      queryKey: schoolKeys.programProgressCells(
        workspaceId,
        calendarLinkedAcademicYear,
        activeTermNumber,
        gradeId
      ),
    });
    void queryClient.invalidateQueries({
      queryKey: schoolKeys.programScopedRows(workspaceId, calendarLinkedAcademicYear, activeTermNumber, gradeId),
    });
    void queryClient.invalidateQueries({
      queryKey: schoolKeys.programFixedRows(workspaceId, calendarLinkedAcademicYear, activeTermNumber, gradeId),
    });
    if (termDef?.starts_on && termDef.ends_on) {
      void queryClient.invalidateQueries({
        queryKey: schoolKeys.calendarDays(workspaceId, termDef.starts_on, termDef.ends_on),
      });
    }
    void queryClient.invalidateQueries({ queryKey: schoolKeys.gradeTrackSections(workspaceId) });
    void queryClient.invalidateQueries({
      queryKey: schoolKeys.annualProjectPrograms(workspaceId, calendarLinkedAcademicYear, gradeId),
    });
  }, [
    activeTermNumber,
    calendarLinkedAcademicYear,
    gradeId,
    queryClient,
    termDef?.ends_on,
    termDef?.starts_on,
    workspaceId,
  ]);

  const prevRefreshNonceRef = useRef(refreshNonce);
  useEffect(() => {
    if (refreshNonce === prevRefreshNonceRef.current) return;
    prevRefreshNonceRef.current = refreshNonce;
    reload();
  }, [refreshNonce, reload]);

  useEffect(() => {
    if (
      forceNetwork &&
      metaQuery.isSuccess &&
      timetableQuery.isSuccess &&
      progressQuery.isSuccess &&
      scopedRowsQuery.isSuccess &&
      fixedRowsQuery.isSuccess
    ) {
      setForceNetwork(false);
    }
  }, [
    fixedRowsQuery.isSuccess,
    forceNetwork,
    metaQuery.isSuccess,
    progressQuery.isSuccess,
    scopedRowsQuery.isSuccess,
    timetableQuery.isSuccess,
  ]);

  /** 대시보드 워밍 시에도 aggregate LS를 남겨 다른 소비자(연간진도 등)가 meta를 찾게 함 */
  useEffect(() => {
    if (!queryEnabled || !metaQuery.data) return;
    const tn = activeTermNumber;
    const ap = loadAnnualProgressDataCache(workspaceId, calendarLinkedAcademicYear, tn);
    saveAnnualProgressDataCache(workspaceId, calendarLinkedAcademicYear, tn, {
      terms: metaQuery.data.terms,
      schedule: metaQuery.data.schedule,
      tracks: metaQuery.data.tracks,
      regularColors: metaQuery.data.regularColors,
      dedicatedColors: metaQuery.data.dedicatedColors,
      baseExcludeColors: metaQuery.data.baseExcludeColors,
      holidayDays: ap.found ? ap.state.holidayDays : [],
      offDayByDate: ap.found ? ap.state.offDayByDate ?? {} : {},
      offDaysHydrated: ap.found ? !!ap.state.offDaysHydrated : false,
      cellsByKey: {},
      holidayHydrated: ap.found ? ap.state.holidayHydrated : false,
      cellsHydrated: true,
    });
  }, [activeTermNumber, calendarLinkedAcademicYear, metaQuery.data, queryEnabled, workspaceId]);

  /** 캘린더 RQ/로컬 학사일정이 오면 활성 학기 AP 캐시에 휴일·휴업·방학을 반영 */
  useEffect(() => {
    if (!queryEnabled || !metaQuery.data) return;
    const calendarDays = !calendarLinkedCtx.useDbCalendarDays
      ? calendarLinkedCtx.localCalendarDays
      : (calendarQuery.data ?? []);
    if (!calendarDays.length && calendarLinkedCtx.useDbCalendarDays && calendarQuery.isPending) return;
    const { holidayDays, offDayByDate } = calendarDaysToOffDayState(calendarDays);
    const ap = loadAnnualProgressDataCache(workspaceId, calendarLinkedAcademicYear, activeTermNumber);
    saveAnnualProgressDataCache(workspaceId, calendarLinkedAcademicYear, activeTermNumber, {
      terms: metaQuery.data.terms,
      schedule: metaQuery.data.schedule,
      tracks: metaQuery.data.tracks,
      regularColors: metaQuery.data.regularColors,
      dedicatedColors: metaQuery.data.dedicatedColors,
      baseExcludeColors: metaQuery.data.baseExcludeColors,
      holidayDays,
      offDayByDate,
      offDaysHydrated: true,
      cellsByKey: ap.found ? ap.state.cellsByKey ?? {} : {},
      holidayHydrated: true,
      cellsHydrated: ap.found ? !!ap.state.cellsHydrated : false,
    });
  }, [
    activeTermNumber,
    calendarLinkedAcademicYear,
    calendarLinkedCtx.localCalendarDays,
    calendarLinkedCtx.useDbCalendarDays,
    calendarQuery.data,
    calendarQuery.isPending,
    metaQuery.data,
    queryEnabled,
    workspaceId,
  ]);

  /**
   * 대시보드가 RQ persist만 채우고 queryFn을 안 타면 LS에 셀이 없음.
   * 성공 데이터를 LS에 미러링해 다른 탭·페이지가 동일 소스를 읽게 한다.
   */
  useEffect(() => {
    if (!queryEnabled) return;
    const rows = timetableQuery.data;
    if (!rows || rows.length === 0) return;
    saveAnnualTimetableGradeCellsLocalCache(
      workspaceId,
      calendarLinkedAcademicYear,
      activeTermNumber,
      gradeId,
      rows
    );
  }, [activeTermNumber, calendarLinkedAcademicYear, gradeId, queryEnabled, timetableQuery.data, workspaceId]);

  useEffect(() => {
    if (!queryEnabled) return;
    const rows = progressQuery.data;
    if (!rows || rows.length === 0) return;
    saveProgramProgressCellsCache(workspaceId, calendarLinkedAcademicYear, activeTermNumber, gradeId, rows, 'full');
  }, [activeTermNumber, calendarLinkedAcademicYear, gradeId, progressQuery.data, queryEnabled, workspaceId]);

  useEffect(() => {
    if (!queryEnabled) return;
    const rows = scopedRowsQuery.data;
    if (!rows || rows.length === 0) return;
    saveAnnualProgressScopedTermCache(
      workspaceId,
      calendarLinkedAcademicYear,
      activeTermNumber,
      gradeId,
      rows
    );
  }, [activeTermNumber, calendarLinkedAcademicYear, gradeId, queryEnabled, scopedRowsQuery.data, workspaceId]);

  const bundle = useMemo((): DashboardLessonSectionBundle | null => {
    if (!queryEnabled || !metaQuery.data) return null;

    const calendarDays = !calendarLinkedCtx.useDbCalendarDays
      ? calendarLinkedCtx.localCalendarDays
      : (calendarQuery.data ?? []);
    const { holidayDays, offDayByDate, dayKindByDate } = calendarDaysToOffDayState(calendarDays);

    const annualRows = timetableQuery.data ?? [];
    const progressRows = progressQuery.data ?? [];
    const scopedRows = scopedRowsQuery.data ?? [];
    const fixedRows = fixedRowsQuery.data ?? [];
    const academicYear = calendarLinkedAcademicYear;
    const termNumber = activeTermNumber;

    const activeTermData: DashboardLessonSectionTermData = {
      termNumber,
      annualRows,
      progressRows,
      scopedRows,
      fixedRows,
      holidayDays,
      offDayByDate,
      dayKindByDate,
    };

    const cachedTerm1 = loadDashboardLessonTermDataFromCaches({
      workspaceId,
      academicYear,
      termNumber: 1,
      gradeId,
    });
    const cachedTerm2 = loadDashboardLessonTermDataFromCaches({
      workspaceId,
      academicYear,
      termNumber: 2,
      gradeId,
    });

    return {
      academicYear,
      terms,
      schedule: metaQuery.data.schedule,
      tracks: metaQuery.data.tracks,
      regularColors: metaQuery.data.regularColors,
      dedicatedColors: metaQuery.data.dedicatedColors,
      baseExcludeColors: metaQuery.data.baseExcludeColors,
      annualProjectPrograms: annualProjectProgramsQuery.data ?? [],
      gradeTrackSections: gradeTrackSectionsQuery.data ?? [],
      byTerm: {
        1:
          termNumber === 1
            ? mergeDashboardLessonTermData(activeTermData, cachedTerm1)
            : cachedTerm1,
        2:
          termNumber === 2
            ? mergeDashboardLessonTermData(activeTermData, cachedTerm2)
            : cachedTerm2,
      },
    };
  }, [
    activeTermNumber,
    annualProjectProgramsQuery.data,
    calendarLinkedAcademicYear,
    calendarLinkedCtx.localCalendarDays,
    calendarLinkedCtx.useDbCalendarDays,
    calendarQuery.data,
    fixedRowsQuery.data,
    gradeTrackSectionsQuery.data,
    metaQuery.data,
    progressQuery.data,
    queryEnabled,
    scopedRowsQuery.data,
    terms,
    timetableQuery.data,
    workspaceId,
    gradeId,
  ]);

  const daySlots = useMemo(() => {
    if (!enabled || !gradeId.trim() || weekdayKey == null || !bundle) return [];
    return buildDashboardAnnualDaySlotsFromLoadedData({
      workspaceId,
      gradeId,
      grades: gradesForSlots,
      mondayIso,
      weekdayKey,
      dateIso,
      bundle,
      termNumber: activeTermNumber,
    });
  }, [activeTermNumber, bundle, dateIso, enabled, gradeId, gradesForSlots, mondayIso, weekdayKey, workspaceId]);

  const loading =
    queryEnabled &&
    (metaQuery.isPending ||
      timetableQuery.isPending ||
      progressQuery.isPending ||
      scopedRowsQuery.isPending ||
      fixedRowsQuery.isPending ||
      calendarQuery.isPending ||
      gradeTrackSectionsQuery.isPending ||
      annualProjectProgramsQuery.isPending);

  /** 메인 프레젠테이션이 재사용할 수 있도록, 화면에 그려질 daySlots를 LS+BroadcastChannel로 미러.
   * loading이 끝나기를 기다리지 않음 — 일부 쿼리 isPending이 남아 있어도 daySlots가 있으면 즉시 저장.
   */
  useEffect(() => {
    if (!queryEnabled || !publishPresentationSnapshot) return;
    if (!dateIso.trim() || !gradeId.trim() || weekdayKey == null) return;
    if (daySlots.length === 0) return;
    const sample = daySlots[0];
    saveDashboardLessonDaySlotsSnapshot({
      workspaceId,
      gradeId,
      dateIso,
      mondayIso,
      weekdayKey,
      academicYear: sample?.academicYear ?? calendarLinkedAcademicYear,
      activeTermNumber: sample?.termNumber ?? activeTermNumber,
      daySlots,
    });
  }, [
    activeTermNumber,
    calendarLinkedAcademicYear,
    dateIso,
    daySlots,
    gradeId,
    mondayIso,
    publishPresentationSnapshot,
    queryEnabled,
    weekdayKey,
    workspaceId,
  ]);

  const error = useMemo(() => {
    for (const q of [
      metaQuery,
      timetableQuery,
      progressQuery,
      scopedRowsQuery,
      fixedRowsQuery,
      calendarQuery,
      gradeTrackSectionsQuery,
      annualProjectProgramsQuery,
    ]) {
      if (q.error) {
        return q.error instanceof Error ? q.error.message : String(q.error);
      }
    }
    return null;
  }, [
    annualProjectProgramsQuery,
    calendarQuery,
    fixedRowsQuery,
    gradeTrackSectionsQuery,
    metaQuery,
    progressQuery,
    scopedRowsQuery,
    timetableQuery,
  ]);

  const resolvedActiveTermNumber: 1 | 2 | null = bundle ? activeTermNumber : null;

  return {
    daySlots,
    loading: enabled ? loading : false,
    error,
    reload,
    activeTermNumber: resolvedActiveTermNumber,
    academicYear: calendarLinkedAcademicYear,
    /** 학기 기간(캐시·RQ meta) — 주차 계산용. 추가 DB 조회 없음 */
    terms,
    /** 위클리 등: 이미 로드된 학기 번들(추가 DB 없이 요일별 슬롯 조립) */
    lessonBundle: bundle,
  };
}
